import { Button } from 'js/design_system/Button';
import React, { useCallback } from 'react';
import Google from 'js/core_post_webpack/google';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'js/core_pre_webpack/routes';
import {
  trackClick,
  Options as ClickTrackingOptions,
} from 'js/utils/trackClick';
import { consoleError } from 'js/utils/console';
import { imageUrl } from 'js/utils/image_url';
import { RegistrationParams } from '../types';

type Props = {
  onAttempt?: () => void;
  onAuthorized?: () => void;
  onSuccess?: (redirect?: string) => void;
  onError: (error?: { errorMessage?: string; errorCodes?: string[] }) => void;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  clickTrackingContext?: Partial<ClickTrackingOptions>;
  buttonText: string;
  registrationParams: RegistrationParams;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onError'>;

export const ContinueWithGoogleButton: React.FC<Props> = (props) => {
  const {
    onAttempt,
    onAuthorized,
    onSuccess,
    onError,
    disabled,
    setLoading,
    clickTrackingContext,
    buttonText,
    registrationParams,
    ...buttonProps
  } = props;

  const handleGoogleAuthButtonClick = useCallback(async () => {
    if (disabled) return;
    try {
      onAttempt && onAttempt();
      setLoading(true);
      trackClick({
        source: 'login_modal_google_oauth_button',
        ...clickTrackingContext,
      });
      const authResponse = await Google.authorize();
      onAuthorized && onAuthorized();
      const data = {
        provider: 'google',
        oauth: {
          token: authResponse.access_token,
          expires: authResponse.expires_in,
          // @ts-ignore at some point they changed `scopes` to `scope`
          scopes: authResponse.scope,
        },
        ...registrationParams,
      };
      const serverResp = await fetchWithCsrf(
        routes.api_v0_register_path({ subdomain_param: 'api' }),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        },
      );
      const respBody = await serverResp.json();
      if (respBody.success) {
        onSuccess && onSuccess(respBody.redirect);
      } else if (respBody.redirect) {
        // This happens if the user created their account with a different OAuth provider
        // than the one they're attempting to log in with.
        window.location.href = respBody.redirect;
      } else if (
        respBody.errors['user[first_name]'] ||
        respBody.errors['user[last_name]']
      ) {
        onError({
          errorMessage:
            'Your Google account is missing a first or last name. \nPlease add one to your Google account and try again, or use a different login method.',
        });
      } else if (respBody.error_codes) {
        onError({ errorCodes: respBody.error_codes });
      } else {
        onError();
      }
    } catch (e) {
      consoleError(e as Error);
      onError();
    }
  }, [
    disabled,
    onAttempt,
    setLoading,
    clickTrackingContext,
    onAuthorized,
    registrationParams,
    onSuccess,
    onError,
  ]);

  return (
    <Button
      secondary
      onClick={handleGoogleAuthButtonClick}
      disabled={disabled}
      {...buttonProps}
    >
      <img src={imageUrl('google/google-color-transparent-bg-2.svg')} />
      {buttonText}
    </Button>
  );
};
